// src/components/tactic/TacticTimeline.jsx
// ─────────────────────────────────────────────────────────────────────────
// Playback transport plus a keyframe track per token.
//
// Editing is auto-key: park the playhead at a time, drag a hero, and that
// becomes a keyframe. The track below is where you see and clean up the path
// you just recorded.
// ─────────────────────────────────────────────────────────────────────────

import React, { useCallback, useRef } from "react";
import { getHeroImageUrlWithFallback } from "../../utils/heroCatalog";
import { hasKeyframeAt } from "../../utils/tactic/playbook";
import { TEAM_COLORS } from "./TacticCanvas";
import { Btn, Row, T } from "./tacticUi";

const SPEEDS = [0.5, 1, 2];

const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}.${Math.floor((t % 1) * 10)}`;

export default function TacticTimeline({
  playbook,
  time,
  onTimeChange,
  playing,
  onPlayingChange,
  speed,
  onSpeedChange,
  selectedIds,
  onSelectToken,
  onToggleKeyframe,
  onDurationChange,
  onAddPhase,
  onRemovePhase,
}) {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const duration = playbook?.duration || 30;
  const tokens = playbook?.tokens || [];
  const phases = playbook?.phases || [];

  const seekFromEvent = useCallback(
    (evt) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const f = Math.max(0, Math.min(1, (evt.clientX - rect.left) / rect.width));
      onTimeChange(Number((f * duration).toFixed(2)));
    },
    [duration, onTimeChange]
  );

  const pct = (t) => `${(Math.max(0, Math.min(duration, t)) / duration) * 100}%`;

  const ticks = [];
  const step = duration <= 20 ? 2 : duration <= 60 ? 5 : 15;
  for (let t = 0; t <= duration; t += step) ticks.push(t);

  const selected = tokens.filter((tk) => selectedIds?.has(tk.id));

  // The button keys the whole selection at once. It only offers to REMOVE when
  // every selected token already sits on a key — a mixed selection resolves
  // towards adding, which is what the eye expects from "+ คีย์เฟรม".
  const allOnKey = selected.length > 0 && selected.every((tk) => hasKeyframeAt(tk.keys, time));
  // Stripping a token's only key would leave it with no position at all, so a
  // removal that would do nothing at all goes quiet instead.
  const stuck = allOnKey && selected.every((tk) => (tk.keys?.length || 0) <= 1);

  return (
    <div
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderRadius: 12,
        padding: 12,
      }}
    >
      <Row style={{ marginBottom: 10 }} gap={6}>
        <Btn onClick={() => onTimeChange(0)} title="กลับไปจุดเริ่ม">⏮</Btn>
        <Btn
          tone="accent"
          onClick={() => onPlayingChange(!playing)}
          style={{ minWidth: 76 }}
        >
          {playing ? "⏸ หยุด" : "▶ เล่น"}
        </Btn>
        <Btn onClick={() => onTimeChange(duration)} title="ไปจุดจบ">⏭</Btn>

        <span
          style={{
            fontFamily: T.mono,
            fontSize: 14,
            color: T.ink,
            padding: "0 8px",
            minWidth: 92,
          }}
        >
          {fmt(time)} <span style={{ color: T.faint }}>/ {fmt(duration)}</span>
        </span>

        {SPEEDS.map((s) => (
          <Btn key={s} tone="ghost" active={speed === s} onClick={() => onSpeedChange(s)} style={{ padding: "5px 8px" }}>
            {s}×
          </Btn>
        ))}

        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11.5, color: T.faint }}>ความยาว</span>
          <input
            type="number"
            min={5}
            max={300}
            value={duration}
            onChange={(e) => onDurationChange(Number(e.target.value) || 30)}
            style={{
              width: 62,
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${T.line}`,
              borderRadius: 7,
              color: T.ink,
              fontFamily: T.mono,
              fontSize: 12.5,
              padding: "5px 7px",
            }}
          />
          <span style={{ fontSize: 11.5, color: T.faint }}>วิ</span>
        </span>

        <Btn
          tone="ghost"
          onClick={onAddPhase}
          title="ตั้งชื่อจังหวะตรงหัวอ่าน เช่น เข้าป่า / ยึดวิชั่น / เข้าตี"
        >
          ⚑ ป้ายจังหวะ
        </Btn>

        <Btn
          tone={allOnKey ? "danger" : "accent"}
          disabled={!selected.length || stuck}
          onClick={() => selected.length && onToggleKeyframe(selected.map((tk) => tk.id))}
          title={
            !selected.length
              ? "เลือกฮีโร่บนแมพก่อน"
              : stuck
              ? "ลบไม่ได้ — ฮีโร่ต้องมีคีย์เฟรมอย่างน้อยหนึ่งจุด"
              : selected.length > 1
              ? `ทำกับทั้ง ${selected.length} ตัวที่เลือก`
              : ""
          }
        >
          {allOnKey ? "ลบคีย์เฟรม" : "+ คีย์เฟรม"}
          {selected.length > 1 && ` (${selected.length})`}
        </Btn>
      </Row>

      {/* Scrubber */}
      <div
        ref={trackRef}
        onPointerDown={(e) => {
          draggingRef.current = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          seekFromEvent(e);
        }}
        onPointerMove={(e) => draggingRef.current && seekFromEvent(e)}
        onPointerUp={() => { draggingRef.current = false; }}
        style={{
          position: "relative",
          height: 30,
          borderRadius: 7,
          background: "rgba(0,0,0,0.4)",
          border: `1px solid ${T.line}`,
          cursor: "pointer",
          overflow: "hidden",
          touchAction: "none",
        }}
      >
        {ticks.map((t) => (
          <div
            key={t}
            style={{
              position: "absolute",
              left: pct(t),
              top: 0,
              bottom: 0,
              width: 1,
              background: "rgba(255,255,255,0.09)",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 3,
                left: 4,
                fontSize: 10,
                color: T.faint,
                fontFamily: T.mono,
              }}
            >
              {t}s
            </span>
          </div>
        ))}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: pct(time),
            background: "rgba(255,16,38,0.13)",
          }}
        />
        {/* Phase flags sit on the scrubber itself, so the beat you are looking
            at is readable without leaving the track. */}
        {phases.map((p) => (
          <div
            key={p.id}
            style={{
              position: "absolute",
              left: pct(p.t),
              top: 0,
              bottom: 0,
              width: 2,
              background: "#a78bfa",
              opacity: 0.9,
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            left: pct(time),
            top: 0,
            bottom: 0,
            width: 2,
            background: T.accent,
            boxShadow: `0 0 8px ${T.accent}`,
          }}
        />
      </div>

      {phases.length > 0 && (
        <Row gap={5} style={{ marginTop: 7 }}>
          {phases.map((p) => (
            <span
              key={p.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                borderRadius: 999,
                border: "1px solid rgba(167,139,250,0.45)",
                background: "rgba(167,139,250,0.12)",
                padding: "2px 4px 2px 9px",
                fontSize: 11.5,
              }}
            >
              <button
                type="button"
                onClick={() => onTimeChange(p.t)}
                title="ไปที่จังหวะนี้"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "#c4b5fd",
                  fontFamily: T.font,
                  fontSize: 11.5,
                }}
              >
                <span style={{ fontFamily: T.mono, opacity: 0.75 }}>{fmt(p.t)}</span>{" "}
                {p.label || "ไม่มีชื่อ"}
              </button>
              <button
                type="button"
                onClick={() => onRemovePhase?.(p.id)}
                title="ลบป้ายนี้"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.faint,
                  fontSize: 13,
                  lineHeight: 1,
                  padding: "0 4px",
                }}
              >
                ×
              </button>
            </span>
          ))}
        </Row>
      )}

      {/* Keyframe tracks */}
      {tokens.length > 0 && (
        <div style={{ marginTop: 8, maxHeight: 148, overflowY: "auto" }}>
          {tokens.map((tok) => {
            const isSel = selectedIds?.has(tok.id);
            return (
              <div
                key={tok.id}
                onClick={(e) =>
                  onSelectToken([tok.id], e.shiftKey || e.metaKey || e.ctrlKey ? "toggle" : "replace")
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "3px 0",
                  cursor: "pointer",
                  opacity: isSel ? 1 : 0.72,
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    border: `2px solid ${TEAM_COLORS[tok.team] || T.dim}`,
                    background: "#0b0d12",
                  }}
                >
                  {tok.kind === "hero" ? (
                    <img
                      src={getHeroImageUrlWithFallback(tok.heroName)}
                      alt={tok.heroName}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : null}
                </span>
                <span
                  style={{
                    width: 78,
                    fontSize: 11.5,
                    color: isSel ? T.ink : T.dim,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flexShrink: 0,
                  }}
                >
                  {tok.heroName || tok.label || "โทเคน"}
                </span>
                <div
                  style={{
                    position: "relative",
                    flex: 1,
                    height: 16,
                    background: isSel ? "rgba(255,16,38,0.07)" : "rgba(255,255,255,0.03)",
                    borderRadius: 4,
                  }}
                >
                  {/* path segments between keys */}
                  {(tok.keys || []).slice(0, -1).map((k, i) => {
                    const next = tok.keys[i + 1];
                    return (
                      <div
                        key={`seg-${i}`}
                        style={{
                          position: "absolute",
                          left: pct(k.t),
                          width: `${((next.t - k.t) / duration) * 100}%`,
                          top: 7,
                          height: 2,
                          background: TEAM_COLORS[tok.team] || T.dim,
                          opacity: 0.5,
                        }}
                      />
                    );
                  })}
                  {(tok.keys || []).map((k) => (
                    <button
                      key={k.t}
                      type="button"
                      title={`${k.t}s`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectToken([tok.id], "replace");
                        onTimeChange(k.t);
                      }}
                      style={{
                        position: "absolute",
                        left: pct(k.t),
                        top: 2,
                        width: 12,
                        height: 12,
                        marginLeft: -6,
                        transform: "rotate(45deg)",
                        background: TEAM_COLORS[tok.team] || T.dim,
                        border: "1px solid rgba(0,0,0,0.6)",
                        borderRadius: 2,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
